import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Trash2, AtSign, Loader2 } from "lucide-react";
import { API_BASE, formatApiError, useAuth } from "./AuthContext";
import { useTeamMembers } from "./AssigneePicker";

const fmtTime = (iso) => {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Replace @handles in `body` with highlighted span; preserves other text verbatim.
function renderBody(body, mentioned, members) {
  const known = new Set(
    mentioned.flatMap((uid) => {
      const m = members.find((x) => x.user_id === uid);
      if (!m) return [];
      return [
        (m.email || "").split("@")[0].toLowerCase(),
        (m.name || "").split(" ")[0].toLowerCase(),
        (m.name || "").replace(/\s+/g, ".").toLowerCase(),
      ].filter(Boolean);
    })
  );
  const parts = [];
  let last = 0;
  const re = /@([A-Za-z][A-Za-z0-9._-]{1,40})/g;
  let m;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    const handle = m[1];
    const hit = known.has(handle.toLowerCase());
    parts.push(
      <span key={`m-${m.index}`}
            className={hit ? "bg-[var(--accent-light)] text-[var(--accent)] rounded px-1 font-medium" : ""}>
        @{handle}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts.length ? parts : body;
}

/**
 * <CommentsPanel kind="lead|client|job|task" entityId={id} />
 *
 * Drop-in collaboration thread with @mentions. Notifies mentioned users.
 */
export default function CommentsPanel({ kind, entityId }) {
  const { state } = useAuth();
  const myId = state?.user?.user_id;
  const members = useTeamMembers();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionPos, setMentionPos] = useState(0);
  const textareaRef = useRef(null);

  const reload = async () => {
    if (!entityId) return;
    try {
      const r = await axios.get(`${API_BASE}/${kind}/${entityId}/comments`);
      setRows(r.data || []);
    } catch (err) {
      // Silent — comments are non-critical UX
      console.warn("comments load failed:", err?.message);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind, entityId]);

  // Compute "@query" position by looking at substring before the caret
  const handleChange = (e) => {
    const v = e.target.value;
    setDraft(v);
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    // Match `@` or `@partial` at end of pre-caret text. Bare `@` opens an
    // empty-query dropdown listing all org members.
    const m = before.match(/@([A-Za-z][A-Za-z0-9._-]*)?$/);
    if (m) {
      setMentionOpen(true);
      setMentionQuery((m[1] || "").toLowerCase());
      setMentionPos(caret - m[0].length);
    } else {
      setMentionOpen(false);
    }
  };

  const filteredMembers = useMemo(() => {
    if (!mentionOpen) return [];
    const q = mentionQuery.toLowerCase();
    return (members || []).filter((m) => {
      const handle = (m.email || "").split("@")[0].toLowerCase();
      const name = (m.name || "").toLowerCase();
      return !q || handle.startsWith(q) || name.startsWith(q) || name.includes(q);
    }).slice(0, 6);
  }, [members, mentionOpen, mentionQuery]);

  const pickMention = (m) => {
    const handle = (m.email || "").split("@")[0] || m.name?.replace(/\s+/g, ".");
    const before = draft.slice(0, mentionPos);
    const afterStart = mentionPos + 1 + mentionQuery.length;
    const after = draft.slice(afterStart);
    const next = `${before}@${handle} ${after}`;
    setDraft(next);
    setMentionOpen(false);
    setTimeout(() => {
      const ta = textareaRef.current;
      if (ta) {
        const pos = before.length + 1 + handle.length + 1;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const send = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/${kind}/${entityId}/comments`,
                                  { body: draft.trim() });
      setRows((p) => [...p, r.data]);
      setDraft("");
      if ((r.data?.mentioned_user_ids || []).length) {
        toast.success(`Mentioned ${r.data.mentioned_user_ids.length} teammate(s)`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API_BASE}/comments/${id}`);
      setRows((p) => p.filter((x) => x.comment_id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-3" data-testid="comments-panel">
      <div className="overline flex items-center gap-1.5"><MessageSquare size={11}/> Activity ({rows.length})</div>
      <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="comments-list">
        {rows.length === 0 && (
          <div className="text-[11px] text-[var(--ink-3)] italic" data-testid="comments-empty">
            No comments yet. Tag a teammate with @ to start a thread.
          </div>
        )}
        {rows.map((c) => (
          <div key={c.comment_id} className="text-[12.5px] border-l-2 border-[var(--border)] pl-3 py-1 group"
               data-testid={`comment-${c.comment_id}`}>
            <div className="flex items-center gap-2 text-[10.5px] text-[var(--ink-3)] mb-0.5">
              <span className="font-medium text-[var(--ink-2)]">{c.author_name || "Someone"}</span>
              <span>·</span>
              <span>{fmtTime(c.created_at)}</span>
              {c.author_user_id === myId && (
                <button onClick={() => del(c.comment_id)}
                        className="ml-auto opacity-0 group-hover:opacity-100 text-[var(--ink-3)] hover:text-[var(--accent)] transition"
                        data-testid={`comment-delete-${c.comment_id}`}>
                  <Trash2 size={10}/>
                </button>
              )}
            </div>
            <div className="text-[var(--ink)] leading-relaxed whitespace-pre-wrap break-words">
              {renderBody(c.body, c.mentioned_user_ids || [], members)}
            </div>
          </div>
        ))}
      </div>
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
            if (e.key === "Escape") setMentionOpen(false);
          }}
          rows={2}
          placeholder="Comment. Use @name to mention someone (⌘+Enter to send)"
          className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[12.5px] focus:outline-none focus:border-[var(--ink)] resize-none"
          data-testid="comments-input"
        />
        {mentionOpen && filteredMembers.length > 0 && (
          <div className="absolute left-0 right-0 -bottom-1 translate-y-full z-10 max-w-[280px] bg-[var(--paper)] border border-[var(--border)] rounded-md shadow-lg overflow-hidden"
               data-testid="comments-mention-dropdown">
            {filteredMembers.map((m) => (
              <button
                key={m.user_id}
                onMouseDown={(e) => { e.preventDefault(); pickMention(m); }}
                className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-[var(--subtle)] flex items-center gap-2"
                data-testid={`comments-mention-${m.user_id}`}
              >
                <AtSign size={10} className="text-[var(--ink-3)]"/>
                <span className="font-medium">{m.name || m.email}</span>
                <span className="text-[10px] text-[var(--ink-3)] font-mono ml-auto">
                  {(m.email || "").split("@")[0]}
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-between items-center mt-1.5">
          <span className="text-[10px] text-[var(--ink-3)]">⌘+Enter to send</span>
          <button onClick={send} disabled={busy || !draft.trim()}
                  className="btn-primary text-[11px] py-1 px-2.5"
                  data-testid="comments-send">
            {busy ? <Loader2 size={11} className="animate-spin"/> : <Send size={11}/>} Post
          </button>
        </div>
      </div>
    </div>
  );
}
